import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ExternalLink, Loader2, Save, Send, Star, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { APP_CONFIG } from '@/config/app';
import { Stars } from '@/components/reviews/Stars';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useTenant } from '@/hooks/useTenant';
import { formatDocDate } from '@/utils/locale';
import {
  DEFAULT_REVIEW_SETTINGS,
  describeSkipReason,
  minutesToUnit,
  unitToMinutes,
  useBrandReviewLinks,
  useReviewRequestLog,
  useReviewRequestSettings,
  useSendTestReviewRequest,
  type ReviewChannel,
  type ReviewLinkMode,
  type ReviewRequestRow,
  type ReviewRequestSettingsInput,
  type StarLinks,
} from '@/hooks/useReviewRequests';

type DelayUnit = 'minutes' | 'hours' | 'days';
type StarKey = keyof StarLinks;

const HOURS = Array.from({ length: 25 }, (_, h) => h);
const STAR_KEYS: StarKey[] = ['1', '2', '3', '4', '5'];

function hourLabel(h: number): string {
  if (h === 0 || h === 24) return '12:00 am';
  if (h === 12) return '12:00 pm';
  return h < 12 ? `${h}:00 am` : `${h - 12}:00 pm`;
}

const STATUS_BADGE: Record<ReviewRequestRow['status'], { label: string; className: string }> = {
  queued: { label: 'Waiting', className: 'bg-info/10 text-info border-info/20' },
  sending: { label: 'Sending', className: 'bg-info/10 text-info border-info/20' },
  sent: { label: 'Sent', className: 'bg-success/10 text-success border-success/20' },
  skipped: { label: 'Skipped', className: 'bg-muted text-muted-foreground' },
  cancelled: { label: 'Cancelled', className: 'bg-muted text-muted-foreground' },
  failed: { label: 'Failed', className: 'bg-destructive/10 text-destructive border-destructive/20' },
};

const CHANNEL_LABEL: Record<ReviewChannel, string> = { sms: 'SMS', email: 'Email', both: 'SMS + email' };

/** GSM-7 segments for the counter; a template with the link is usually 2. */
function smsSegments(text: string): number {
  const len = text.length;
  if (len === 0) return 0;
  return len <= 160 ? 1 : Math.ceil(len / 153);
}

const SAMPLE_LINK_DIRECT = 'https://g.page/r/…';
function preview(template: string, company: string, link: string): string {
  return template
    .replace(/\{first_name\}/gi, 'Jane')
    .replace(/\{company\}/gi, company || 'Your trading name')
    .replace(/\{link\}/gi, link);
}

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value.trim());
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Settings → Review requests: the automation, its wording, where the link goes, and what it has done. */
export function ReviewRequests() {
  const { company, isAdmin, tenantId } = useTenant();
  const { locale, timezone } = useCompanyLocale();
  const { settings, isLoading, save, isSaving } = useReviewRequestSettings();
  const { brands, isLoading: loadingBrands, update: updateBrand } = useBrandReviewLinks();
  const { rows, isLoading: loadingLog, cancel, sendNow, isSending } = useReviewRequestLog();
  const testSend = useSendTestReviewRequest();

  const [form, setForm] = useState<ReviewRequestSettingsInput>(DEFAULT_REVIEW_SETTINGS);
  const [delayValue, setDelayValue] = useState<string>('2');
  const [delayUnit, setDelayUnit] = useState<DelayUnit>('hours');
  const [dirty, setDirty] = useState(false);
  const [brandDrafts, setBrandDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isLoading) return;
    const next: ReviewRequestSettingsInput = settings
      ? {
          is_enabled: settings.is_enabled,
          delay_minutes: settings.delay_minutes,
          channel: settings.channel,
          review_url: settings.review_url,
          sms_template: settings.sms_template,
          email_subject: settings.email_subject,
          email_template: settings.email_template,
          quiet_hours_start: settings.quiet_hours_start,
          quiet_hours_end: settings.quiet_hours_end,
          cooldown_days: settings.cooldown_days,
          link_mode: settings.link_mode ?? 'direct',
          star_links: settings.star_links ?? {},
          star_prompt: settings.star_prompt ?? 'How did we do?',
        }
      : DEFAULT_REVIEW_SETTINGS;
    setForm(next);
    const u = minutesToUnit(next.delay_minutes);
    setDelayValue(String(u.value));
    setDelayUnit(u.unit);
    setDirty(false);
  }, [settings, isLoading]);

  useEffect(() => {
    setBrandDrafts(Object.fromEntries(brands.map(b => [b.id, b.review_url ?? ''])));
  }, [brands]);

  const set = <K extends keyof ReviewRequestSettingsInput>(key: K, value: ReviewRequestSettingsInput[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };
  const setStarLink = (k: StarKey, value: string) => {
    setForm(prev => {
      const next: StarLinks = { ...prev.star_links };
      if (value.trim()) next[k] = value; else delete next[k];
      return { ...prev, star_links: next };
    });
    setDirty(true);
  };

  const starsMode = form.link_mode === 'stars';
  const anyBrandLink = brands.some(b => b.review_url && isHttpUrl(b.review_url));
  const companyLinkOk = !!form.review_url && isHttpUrl(form.review_url);
  const linkMissing = form.is_enabled && !starsMode && !companyLinkOk && !anyBrandLink;
  const delayMinutes = unitToMinutes(Number(delayValue), delayUnit);
  const defaultBrandName = brands.find(b => b.is_default)?.name ?? company?.name ?? '';
  const previewUrl = tenantId ? `${APP_CONFIG.appUrl}/r/preview-${tenantId}` : null;
  const sampleLink = starsMode ? `${APP_CONFIG.appUrl}/r/a1b2c3…` : SAMPLE_LINK_DIRECT;

  const fmtWhen = (value: string | null) =>
    value ? formatDocDate(value, `${locale.shortDate}, h:mm a`, timezone) : '—';

  const handleSave = async () => {
    if (form.review_url && !companyLinkOk) {
      toast.error('The review link must start with https://');
      return;
    }
    for (const k of STAR_KEYS) {
      const v = form.star_links[k];
      if (v && !isHttpUrl(v)) {
        toast.error(`The ${k}-star link must start with https://`);
        return;
      }
    }
    if (delayMinutes > 43200) {
      toast.error('The delay can be at most 30 days');
      return;
    }
    if (form.quiet_hours_end <= form.quiet_hours_start) {
      toast.error('The sending window must end after it starts');
      return;
    }
    if (!form.sms_template.includes('{link}') && form.channel !== 'email') {
      toast.error('The SMS needs the {link} placeholder, or the customer has nowhere to go');
      return;
    }
    if (!form.email_template.includes('{link}') && form.channel !== 'sms') {
      toast.error('The email needs the {link} placeholder, or the customer has nowhere to go');
      return;
    }
    try {
      const cleanedStars: StarLinks = {};
      for (const k of STAR_KEYS) {
        const v = form.star_links[k]?.trim();
        if (v) cleanedStars[k] = v;
      }
      await save({
        ...form,
        delay_minutes: delayMinutes,
        review_url: form.review_url?.trim() || null,
        star_links: cleanedStars,
        star_prompt: form.star_prompt.trim() || 'How did we do?',
      });
      setDirty(false);
      toast.success(form.is_enabled ? 'Review requests are on' : 'Settings saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save');
    }
  };

  const handleTest = async () => {
    try {
      const r = await testSend.mutateAsync({ channel: form.channel });
      const parts = Object.entries(r.results).map(([k, v]) => `${k.toUpperCase()}: ${v}`);
      const failed = parts.some(p => /failed|skipped/i.test(p));
      (failed ? toast.warning : toast.success)('Test send', { description: parts.join(' · '), duration: 8000 });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not send the test');
    }
  };

  const handleBrandBlur = async (id: string) => {
    const draft = (brandDrafts[id] ?? '').trim();
    const current = brands.find(b => b.id === id)?.review_url ?? '';
    if (draft === (current ?? '')) return;
    if (draft && !isHttpUrl(draft)) {
      toast.error('The link must start with https://');
      return;
    }
    try {
      await updateBrand({ id, review_url: draft || null });
      toast.success('Link saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the link');
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await cancel(id);
      toast.success('Request cancelled');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not cancel');
    }
  };

  const handleSendNow = async (id: string) => {
    try {
      const r = await sendNow(id);
      if (r.ok) toast.success('Review request sent');
      else toast.warning('Not sent', { description: describeSkipReason(r.outcome.reason) || r.outcome.error || r.outcome.status });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not send');
    }
  };

  const queuedCount = useMemo(() => rows.filter(r => r.status === 'queued').length, [rows]);
  const ratedRows = useMemo(() => rows.filter(r => r.rating != null), [rows]);
  const avgRating = ratedRows.length
    ? ratedRows.reduce((sum, r) => sum + (r.rating ?? 0), 0) / ratedRows.length
    : null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Review requests</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Ask the customer for a review once their invoice is paid in full. One message per invoice, never twice to the
            same customer within the cooldown, never to a refunded or disputed payment.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!isAdmin || isSaving || !dirty} className="shrink-0">
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save
        </Button>
      </div>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">Automation</CardTitle>
              <CardDescription>When and how the request goes out.</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="rr-enabled" className="text-sm">{form.is_enabled ? 'On' : 'Off'}</Label>
              <Switch id="rr-enabled" checked={form.is_enabled} onCheckedChange={v => set('is_enabled', v)} disabled={!isAdmin} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {linkMissing && (
            <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm">
              Add a review link below before turning this on. Without one, every request is skipped.
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Send after payment</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={delayValue}
                  onChange={e => { setDelayValue(e.target.value); setDirty(true); }}
                  className="w-24"
                  disabled={!isAdmin}
                />
                <Select value={delayUnit} onValueChange={v => { setDelayUnit(v as DelayUnit); setDirty(true); }} disabled={!isAdmin}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">minutes</SelectItem>
                    <SelectItem value="hours">hours</SelectItem>
                    <SelectItem value="days">days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                {delayMinutes === 0 ? 'Goes out on the next run, within 5 minutes.' : 'Checked every 5 minutes, so allow up to 5 minutes on top.'}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Channel</Label>
              <Select value={form.channel} onValueChange={v => set('channel', v as ReviewChannel)} disabled={!isAdmin}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="both">SMS and email</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                SMS goes from the number the customer called, else your default caller ID. Email uses your company sender.
              </p>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Only send between</Label>
              <div className="flex items-center gap-2">
                <Select value={String(form.quiet_hours_start)} onValueChange={v => set('quiet_hours_start', Number(v))} disabled={!isAdmin}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HOURS.filter(h => h < 24).map(h => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">and</span>
                <Select value={String(form.quiet_hours_end)} onValueChange={v => set('quiet_hours_end', Number(v))} disabled={!isAdmin}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HOURS.filter(h => h > 0).map(h => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Company time ({timezone}). Anything due outside this window waits for the next morning.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rr-cooldown">Do not ask the same customer again for</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="rr-cooldown"
                  type="number"
                  min={0}
                  max={3650}
                  value={form.cooldown_days}
                  onChange={e => set('cooldown_days', Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                  className="w-24"
                  disabled={!isAdmin}
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
              <p className="text-xs text-muted-foreground">0 asks after every paid invoice.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">Where the link goes</CardTitle>
              <CardDescription>What {'{link}'} opens when the customer taps it.</CardDescription>
            </div>
            {starsMode && previewUrl && (
              <Button variant="outline" size="sm" asChild>
                <a href={previewUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" /> Preview page
                </a>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <Select value={form.link_mode} onValueChange={v => set('link_mode', v as ReviewLinkMode)} disabled={!isAdmin}>
              <SelectTrigger className="sm:max-w-md"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">Straight to a review site</SelectItem>
                <SelectItem value="stars">A 5-star page, with a link for each star</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!starsMode ? (
            <div className="space-y-1.5">
              <Label htmlFor="rr-url">Review link</Label>
              <Input
                id="rr-url"
                placeholder="https://g.page/r/…/review"
                value={form.review_url ?? ''}
                onChange={e => set('review_url', e.target.value)}
                disabled={!isAdmin}
              />
              <p className="text-xs text-muted-foreground">
                In your Google Business Profile choose "Ask for reviews" and copy the short link. Trading names can have their own link below.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="rr-prompt">Question on the page</Label>
                <Input id="rr-prompt" value={form.star_prompt} onChange={e => set('star_prompt', e.target.value)} maxLength={80} disabled={!isAdmin} />
              </div>
              <div className="space-y-2">
                <Label>After the customer taps a star, send them to</Label>
                {STAR_KEYS.map(k => (
                  <div key={k} className="grid items-center gap-2 sm:grid-cols-[120px_1fr]">
                    <Stars n={Number(k)} />
                    <Input
                      placeholder="Leave blank for a thank-you screen with a comment box"
                      value={form.star_links[k] ?? ''}
                      onChange={e => setStarLink(k, e.target.value)}
                      disabled={!isAdmin}
                    />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  The page shows your trading name and logo. Every rating is recorded on the job and listed below, whichever link it opens.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">Messages</CardTitle>
              <CardDescription>
                Placeholders: <code className="rounded bg-muted px-1">{'{first_name}'}</code>{' '}
                <code className="rounded bg-muted px-1">{'{company}'}</code>{' '}
                <code className="rounded bg-muted px-1">{'{link}'}</code>. Technician names are never included.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleTest}
              disabled={!isAdmin || !settings || dirty || testSend.isPending}
              title={dirty ? 'Save first, then send a test' : 'Sends the message to your own phone and email'}
            >
              {testSend.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Send me a test
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {form.channel !== 'email' && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="rr-sms">SMS</Label>
                <span className="text-xs text-muted-foreground">
                  {form.sms_template.length} characters · about {smsSegments(preview(form.sms_template, defaultBrandName, sampleLink))} message{smsSegments(preview(form.sms_template, defaultBrandName, sampleLink)) === 1 ? '' : 's'}
                </span>
              </div>
              <Textarea id="rr-sms" rows={3} value={form.sms_template} onChange={e => set('sms_template', e.target.value)} disabled={!isAdmin} />
              <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground whitespace-pre-wrap">
                {preview(form.sms_template, defaultBrandName, sampleLink)}
              </p>
            </div>
          )}

          {form.channel !== 'sms' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="rr-subject">Email subject</Label>
                <Input id="rr-subject" value={form.email_subject} onChange={e => set('email_subject', e.target.value)} disabled={!isAdmin} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rr-email">Email body</Label>
                <Textarea id="rr-email" rows={7} value={form.email_template} onChange={e => set('email_template', e.target.value)} disabled={!isAdmin} />
                <p className="text-xs text-muted-foreground">
                  The line holding {'{link}'} becomes a "Leave a review" button. Under the message we add your trading name,
                  phone, website, address and business number from Company Profile, and an unsubscribe link. Customers who
                  unsubscribe, or reply STOP to the text, are never asked again.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {!starsMode && !loadingBrands && brands.length > 1 && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Links by trading name</CardTitle>
            <CardDescription>
              A customer is asked to review the trading name their job ran under. Leave blank to use the link above.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {brands.map(b => (
              <div key={b.id} className="grid items-center gap-2 sm:grid-cols-[200px_1fr]">
                <Label className="flex items-center gap-2">
                  {b.name}
                  {b.is_default && <Badge variant="outline" className="text-[10px]">Default</Badge>}
                </Label>
                <Input
                  placeholder="Uses the company link"
                  value={brandDrafts[b.id] ?? ''}
                  onChange={e => setBrandDrafts(prev => ({ ...prev, [b.id]: e.target.value }))}
                  onBlur={() => handleBrandBlur(b.id)}
                  disabled={!isAdmin}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">Recent activity</CardTitle>
              <CardDescription>
                {queuedCount > 0 ? `${queuedCount} waiting to go out.` : 'Requests queued and sent, newest first.'}
              </CardDescription>
            </div>
            {avgRating != null && (
              <div className="flex items-center gap-2 text-sm">
                <Stars n={Math.round(avgRating)} />
                <span className="font-medium">{avgRating.toFixed(1)}</span>
                <span className="text-muted-foreground">from {ratedRows.length} rating{ratedRows.length === 1 ? '' : 's'}</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingLog ? (
            <Skeleton className="h-24 w-full" />
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
              <Star className="h-8 w-8 opacity-30" />
              <p className="text-sm">Nothing yet. The first fully paid invoice after you turn this on will show up here.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => {
                  const badge = STATUS_BADGE[r.status];
                  const detail =
                    r.status === 'sent'
                      ? [r.to_phone, r.to_email].filter(Boolean).join(' · ')
                      : r.status === 'failed'
                        ? r.error ?? ''
                        : describeSkipReason(r.skip_reason);
                  // Email funnel after the send: opened → clicked (Postmark events).
                  const funnel =
                    r.status === 'sent' && r.to_email
                      ? r.email_clicked_at
                        ? `Opened · Clicked ${fmtWhen(r.email_clicked_at)}`
                        : r.email_opened_at
                          ? `Opened ${fmtWhen(r.email_opened_at)}`
                          : 'Not opened yet'
                      : null;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">
                        {r.customer_id ? (
                          <Link to={`/customers/${r.customer_id}`} className="text-primary hover:underline">
                            {r.customers?.customer_name ?? 'Customer'}
                          </Link>
                        ) : (r.customers?.customer_name ?? '—')}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{r.invoices?.invoice_number ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{CHANNEL_LABEL[r.channel]}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <Badge variant="outline" className={`w-fit ${badge.className}`}>{badge.label}</Badge>
                          {detail && <span className="text-xs text-muted-foreground max-w-[260px] truncate" title={detail}>{detail}</span>}
                          {funnel && (
                            <span className={`text-xs ${r.email_clicked_at ? 'text-success' : r.email_opened_at ? 'text-info' : 'text-muted-foreground'}`}>
                              {funnel}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.rating != null ? (
                          <div className="flex flex-col gap-0.5">
                            <Stars n={r.rating} />
                            {r.rating_comment && (
                              <span className="text-xs text-muted-foreground max-w-[260px] truncate" title={r.rating_comment}>“{r.rating_comment}”</span>
                            )}
                          </div>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {r.status === 'sent' ? fmtWhen(r.sent_at) : r.status === 'queued' ? fmtWhen(r.send_after) : fmtWhen(r.created_at)}
                      </TableCell>
                      <TableCell>
                        {r.status === 'queued' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleSendNow(r.id)} disabled={isSending} title="Send now">
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleCancel(r.id)} title="Cancel">
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ReviewRequests;
